/**
 * Browser session state — single source of truth.
 *
 * Credentials are cleared in exactly one place (`clearSession`) and only once
 * the user has actually reached the login screen. Clearing them earlier (e.g.
 * inside a logout click handler) breaks any flow that can intercept the
 * navigation — the Workbench unsaved-work blocker keeps the officer on the
 * page while every subsequent API call is already unauthenticated.
 */

const TOKEN_KEY = 'token';
const USER_KEY = 'user';

/** Decode JWT payload without a library — just base64 decode the middle segment. */
export function parseTokenExpiry(token: string): number | null {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp ? payload.exp * 1000 : null; // convert to ms
    } catch {
        return null; // malformed
    }
}

export function getToken(): string | null {
    try {
        return localStorage.getItem(TOKEN_KEY);
    } catch {
        return null;
    }
}

/** True when a token exists, parses, and has not expired. */
export function hasActiveSession(): boolean {
    const token = getToken();
    if (!token) return false;
    const expiresAt = parseTokenExpiry(token);
    return expiresAt !== null && expiresAt > Date.now();
}

export type ClearSessionOptions = {
    /** Keep entity stats + supported chains warm for faster OTP re-login. */
    preservePublicCatalog?: boolean;
};

export function clearSession(opts?: ClearSessionOptions): void {
    try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
    } catch {
        /* storage unavailable — nothing to clear */
    }
    void import('./dashboardCache').then(({ clearDashboardCache, clearPrivateDashboardCache }) => {
        if (opts?.preservePublicCatalog) {
            clearPrivateDashboardCache();
        } else {
            clearDashboardCache();
        }
    }).catch(() => {});
}

/**
 * Set while the app itself is sending the browser to the login screen.
 *
 * `beforeunload` guards must not call `preventDefault()` during such a
 * navigation: the native "Leave site?" prompt can cancel our redirect, which
 * strands the app in a signed-out-but-still-rendered state where every request
 * fails. Guards should still persist their draft before letting the unload run.
 */
let appNavigating = false;

export function beginAppNavigation(): void {
    appNavigating = true;
}

export function isAppNavigating(): boolean {
    return appNavigating;
}

/** Test-only reset so the module-level flag does not leak between cases. */
export function resetAppNavigationForTests(): void {
    appNavigating = false;
}

/** Message shown wherever an action fails because the session is gone. */
export const SESSION_ENDED_MESSAGE =
    'Your session has ended. Sign in again to save this investigation.';

/** sessionStorage flag: restore Workbench canvas after idle OTP re-login. */
export const WORKBENCH_RESTORE_AFTER_AUTH_KEY = 'workbench-restore-after-auth';

/** Set on the login screen when the officer arrived via expiry / OTP. */
export function markWorkbenchRestoreAfterAuth(): void {
    try {
        sessionStorage.setItem(WORKBENCH_RESTORE_AFTER_AUTH_KEY, '1');
    } catch {
        /* private mode */
    }
}

/** True once; clears the flag so a later Workbench navigation is not auto-restored. */
export function consumeWorkbenchRestoreAfterAuth(): boolean {
    try {
        const hit = sessionStorage.getItem(WORKBENCH_RESTORE_AFTER_AUTH_KEY) === '1';
        if (hit) sessionStorage.removeItem(WORKBENCH_RESTORE_AFTER_AUTH_KEY);
        return hit;
    } catch {
        return false;
    }
}

